import { commandAddresseeCode, seatIsAwaited } from '@pams/shared';
import type {
  FillCommandFamilyDto,
  FillCommandSeatDto,
  FillSheetDto,
  HandDownCommandSeatsDto,
  PostAudience,
} from '@pams/shared';
import type { CommandObligation } from './fillPost';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a §4] THE FILL SCREEN'S COMMAND FAMILIES, READ — pure, and kept out of every force file.
 *
 *  A family is one command role asked of one division (`fillCommandFamilySchema`), and every seat in
 *  it is ONE position. This file answers the three questions the fill screen asks of them, and it
 *  answers each as a yes/no or a set of words — never as a figure beside the force:
 *
 *    · does the reading DIVISION still owe a position?  (`commandObligation` → `sheetDischarged`)
 *    · which roles are awaited, and from whom?           (`awaitedCommands` → the footer's notice)
 *    · which EMPTY seats move where, for a hand-down?    (`handDownTargets` → the one write)
 *
 *  ⚠️ IT LIVES HERE AND NOT IN `fillPost.ts`, WHICH STAYS FORCE-ONLY. `sheetDischarged` takes the
 *  obligation as two booleans and never learns what a family is, so `postSummary`, the footer's
 *  `n of m named`, `countNamed`/`countSlots` and every other number on the screen are computed from
 *  force lines alone — `commandIsolation.test.ts` (i)'s census holds the force-counting files off
 *  the vocabulary, and its (ii) holds `sheetDischarged`'s lines to force lines.
 *
 *  ⚠️ "AWAITED" IS `seatIsAwaited`, THE ONE TYPESCRIPT ANSWER in `@pams/shared` — never a second
 *  `officer === null` written here.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ A POSITION THE DIVISION ITSELF MAY MOVE — still with it, or handed on by it. The HAND-DOWN's
 * question, and only the hand-down's (`movableSeats`).
 *
 * A seat the ORDER asked of a unit directly is neither: `handedDown` is false and its addressee is the
 * unit, and the server refuses to let the division move it ("not this division's to hand on").
 *
 * ⚠️ IT IS NOT THE "DONE" QUESTION, and `commandObligation` does not read it. Not movable is not
 * discharged: the division still sees that position, can name into it from this screen, and the force
 * half counts a row the order asked of a unit directly against the division ([C6a §4], below).
 */
const askedOfDivision = (family: FillCommandFamilyDto, seat: FillCommandSeatDto): boolean =>
  seat.handedDown || seat.addressee.id === family.division.id;

/** Still the division's own to name: addressed to the division itself, not handed to a unit. */
const heldByDivision = (family: FillCommandFamilyDto, seat: FillCommandSeatDto): boolean =>
  !seat.handedDown && seat.addressee.id === family.division.id;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ WHAT THE READING DIVISION OWES IN COMMAND — the fourth, REQUIRED argument of `sheetDischarged`.
 *
 *  `asked`: the sheet carries a position at all. `outstanding`: one of them is empty and was NOT handed
 *  on by the division. The two readings are the force half's, one for one:
 *
 *    · a position the division HANDED to a unit is the unit's to fill and no longer holds the
 *      division's part open — `postSummary` counts a handed-down row as discharged ([UX1 §3]);
 *    · a position the ORDER asked of a unit directly still does — `postSummary` counts a `CD:BOW` row
 *      the CP office asked directly as NOT delegated (its parent key is null), so its empty slots hold
 *      the force pill shut. The division sees it and may name into it from this very screen.
 *
 *  ⚠️ SO `askedOfDivision` IS NOT READ HERE. It answers what the division may MOVE, which a direct unit
 *  ask is not — and reading "not movable" as "not owed" called a division done beside an empty
 *  In-charge the order had asked of one of its own stations, the unsafe direction for a "done" signal.
 *
 *  ⚠️ EVERY FAMILY THE SHEET CARRIES IS COUNTED AS THE READER'S. The server shows a family only where
 *  the seat's own upward sight reaches the caller, and for a DIVISION reader — the only one
 *  `sheetDischarged` answers for — that is its own division's families. A family that somehow was not
 *  would only hold the pill shut, which is the safe direction for a "done" signal.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const commandObligation = (families: readonly FillCommandFamilyDto[]): CommandObligation => ({
  asked: families.some((family) => family.seats[0] !== undefined),
  outstanding: families.some((family) => family.seats.some((seat) => !seat.handedDown && seatIsAwaited(seat))),
});

/**
 * One awaited role, as words: `from === null` means "the reader's own to name" and reads "still to be
 * named"; otherwise it is the addressee's codes — `CD`, or `CD · TALA` once handed down — in
 * `commandAddresseeCode`'s register, which the bell and the printed order say the same way.
 */
export type AwaitedCommand = { label: string; from: string | null };

/**
 * ⚠️ EVERY AWAITED POSITION THE READER CAN SEE, ONE ENTRY PER ROLE AND ADDRESSEE — never one per seat.
 * Two empty positions asked of Central read "awaited from CD" once: a statement, not a count spelled as
 * identities (`awaitedCommandSchema`'s own rule).
 *
 * ⚠️ IT IS SHOWN WHETHER OR NOT THE FORCE SHARE IS LOCKED. `autoCompleteFullShares` locks the force the
 * moment its last slot is named, and a footer reading `🔒 All Filled` over an unnamed In-charge is the
 * false "done" this notice exists to prevent.
 */
export const awaitedCommands = (
  families: readonly FillCommandFamilyDto[],
  audience: PostAudience,
): AwaitedCommand[] => {
  const seen = new Map<string, AwaitedCommand>();
  for (const family of families) {
    for (const seat of family.seats.filter(seatIsAwaited)) {
      const ownDivision = seat.addressee.id === family.division.id;
      const from =
        audience === 'addressee' && ownDivision
          ? null
          : commandAddresseeCode(family.division, ownDivision ? null : seat.addressee);
      seen.set(`${family.label}|${from ?? ''}`, { label: family.label, from });
    }
  }
  return [...seen.values()];
};

// ── Hand to a unit ────────────────────────────────────────────────────────────────

/** One row of the hand-down editor: a unit and how many of this role's positions sit with it. */
export type HandDownRow = { target: string | null; count: number };

/** The seats that may take part in a hand-down: asked of the division, held by it or by its units. */
const movableSeats = (family: FillCommandFamilyDto): FillCommandSeatDto[] =>
  family.seats.filter((seat) => askedOfDivision(family, seat));

const handedTo = (family: FillCommandFamilyDto): Map<string, FillCommandSeatDto[]> => {
  const byUnit = new Map<string, FillCommandSeatDto[]>();
  for (const seat of movableSeats(family).filter((row) => row.handedDown)) {
    byUnit.set(seat.addressee.id, [...(byUnit.get(seat.addressee.id) ?? []), seat]);
  }
  return byUnit;
};

/**
 * ⚠️ THE EDITOR'S SEED — what this family has ALREADY handed on, one row per unit. Opening the editor
 * on a family that has handed nothing starts on one blank row; opening it after a hand-down shows that
 * hand-down, so confirming it unchanged moves nothing.
 */
export const handDownSeed = (family: FillCommandFamilyDto): { target: string; count: number }[] =>
  [...handedTo(family)].map(([unitId, seats]) => ({ target: unitId, count: seats.length }));

/** ⚠️ A NAMED POSITION NEVER MOVES, so a unit's row cannot go below the names already standing on it. */
export const handDownFloor = (family: FillCommandFamilyDto, unitId: string | null): number =>
  unitId === null ? 0 : (handedTo(family).get(unitId) ?? []).filter((seat) => !seatIsAwaited(seat)).length;

/** How many positions the units may hold between them: every movable one, less those named at the division. */
export const handDownCapacity = (family: FillCommandFamilyDto): number =>
  movableSeats(family).filter((seat) => !(heldByDivision(family, seat) && !seatIsAwaited(seat))).length;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ ROWS → SPECIFIC SEATS. THE SERVER MOVES SEATS BY ID, SO WHICH EMPTY SEAT GOES WHERE IS DECIDED
 *  HERE, AND ONLY EMPTY ONES EVER MOVE.
 *
 *  A unit holding more than its row now asks releases that many EMPTY seats; a unit asking more takes
 *  them first — straight from the unit that let them go — and then empty seats still with the division.
 *  Whatever is released and not taken goes back to the division (`unitOrgUnitId: null`). A seat whose
 *  place does not change is not listed, so the write touches only what moved ("a seat not listed is
 *  left exactly where it is").
 *
 *  `null` when the rows cannot be met — a unit asked below the names standing on it, or more positions
 *  asked of the units than there are empty ones to move — so Confirm is refused rather than posting a
 *  split the server would 409.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const handDownTargets = (
  family: FillCommandFamilyDto,
  rows: readonly HandDownRow[],
): HandDownCommandSeatsDto['targets'] | null => {
  const wanted = new Map<string, number>();
  for (const row of rows) {
    if (row.target === null) continue;
    if (!Number.isInteger(row.count) || row.count < 0) return null;
    wanted.set(row.target, (wanted.get(row.target) ?? 0) + row.count);
  }

  const held = handedTo(family);
  const released: FillCommandSeatDto[] = [];
  for (const [unitId, seats] of held) {
    const surplus = seats.length - (wanted.get(unitId) ?? 0);
    if (surplus <= 0) continue;
    const empty = seats.filter(seatIsAwaited);
    if (empty.length < surplus) return null;
    released.push(...empty.slice(0, surplus));
  }
  const atDivision = movableSeats(family).filter((seat) => heldByDivision(family, seat) && seatIsAwaited(seat));

  const targets: HandDownCommandSeatsDto['targets'] = [];
  for (const [unitId, want] of wanted) {
    for (let need = want - (held.get(unitId) ?? []).length; need > 0; need -= 1) {
      const seat = released.shift() ?? atDivision.shift();
      if (!seat) return null;
      targets.push({ seatId: seat.seatId, unitOrgUnitId: unitId });
    }
  }
  for (const seat of released) targets.push({ seatId: seat.seatId, unitOrgUnitId: null });
  return targets;
};

/**
 * ⚠️ A SEAT WRITE'S ANSWER, WRITTEN BACK ONTO THE SHEET — `setQueryData`'s updater, never an
 * invalidation (`fillKeyIsolation.test.ts`). Every command verb answers with the caller's families on
 * this duty, narrowed by the same `?division=` the sheet was, and nothing else on the sheet can have
 * moved: the verbs touch no force line, share or slot. Empty is written as ABSENT, the key's own
 * convention on the wire, so a sheet that loses its last family reads exactly as one that never had one.
 */
export const withCommandFamilies = (
  sheet: FillSheetDto | undefined,
  families: readonly FillCommandFamilyDto[],
): FillSheetDto | undefined => {
  if (!sheet) return sheet;
  const { commandFamilies: _previous, ...rest } = sheet;
  return families[0] === undefined ? rest : { ...rest, commandFamilies: [...families] };
};
